import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useFinishImportLegalMattersMutation, useImportLegalMattersMutation } from '../../../../../queries';
import { BreadcrumbBar } from '../../../../components-v2/BreadcrumbBar';
import { ROUTES } from '../../../../constants/routes';
import { baseStyleSheet } from '../../../../styles/styles';
import { MultiContainerHeader } from '../../../MultiContainerHeader';
import { MapStep } from '../../Steps/Map';
import { ProcessStep } from '../../Steps/Process';
import { UploadStep } from '../../Steps/Upload';
import { styleSheet } from './styles';

enum Step {
	Uploading,
	Mapping,
	Processing,
}

export const MattersImport = () => {
	const history = useHistory();
	const userSession = useUserSession();
	const [file, setFile] = React.useState<File | null>(null);
	const [currentStep, setCurrentStep] = React.useState(Step.Uploading);
	const [systemJob, setSystemJob] = React.useState<Api.ISystemJob | null>(null);
	const [fields, setFields] = React.useState<Api.IImportLegalMatterFieldConfig[] | null>(null);
	const [headers, setHeaders] = React.useState<string[] | null>(null);
	const [samples, setSamples] = React.useState<string[][] | null>(null);
	const [mappings, setMappings] = React.useState<Api.IDictionary<string>>({});
	const [mappingErrors, setMappingErrors] = React.useState<string[]>([]);
	const [mergeByName, setMergeByName] = React.useState<boolean>(true);

	const handleDownloadSample = React.useCallback(() => {
		userSession.webServiceHelper.callWebServiceRawResponse(
			'legalMatter/sampleCsv',
			'GET',
			null,
			response => {
				response.blob().then(blob => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.href = url;
					a.download = 'legal-matters-sample.csv';
					document.body.appendChild(a);
					a.click();
					a.remove();
					window.URL.revokeObjectURL(url);
				});
			},
			error => {
				console.error('Failed to download sample CSV:', error);
			}
		);
	}, [userSession.webServiceHelper]);

	const importMattersMutation = useImportLegalMattersMutation({
		onSuccess: (result: Api.IImportLegalMatterPreview) => {
			setSystemJob({ id: result.id } as Api.ISystemJob);
			setFields(result.fields);
			setHeaders(result.header);
			setSamples(result.sample);
			setCurrentStep(currentStep + 1);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to upload file');
		},
	});

	const finishImportMattersMutation = useFinishImportLegalMattersMutation({
		onSuccess: (newSystemJob: Api.ISystemJob) => {
			setSystemJob(newSystemJob);
			setCurrentStep(currentStep + 1);
		},
		onError: (error: Api.IOperationResultNoValue) => {
			toast.error(error.systemMessage || 'Failed to import matters');
		},
	});

	const next = async () => {
		switch (currentStep) {
			case Step.Uploading:
				if (file) {
					importMattersMutation.mutate({ file });
				}
				break;
			case Step.Mapping:
				if (systemJob) {
					finishImportMattersMutation.mutate({
						systemJobId: systemJob.id ?? '',
						options: { fieldMapping: mappings, allowMatchingByName: mergeByName },
					});
				}
				break;
			default:
				throw new Error('Impossible scenario');
		}
	};

	const isUploading = currentStep === Step.Uploading;
	const isMapping = currentStep === Step.Mapping;
	const isProcessing = currentStep === Step.Processing;

	const canGoNext = React.useMemo(() => {
		if (isUploading) {
			return !!file;
		} else if (isMapping) {
			return mappingErrors.length === 0;
		} else {
			return false;
		}
	}, [isUploading, isMapping, file, mappingErrors]);

	const importError = finishImportMattersMutation.error?.systemMessage;

	const onMergeByNameChange = React.useCallback((checked: boolean) => {
		setMergeByName(checked);
	}, []);

	const handleGoToMattersBoard = React.useCallback(() => {
		history.push(ROUTES.dataBoards.matters);
	}, [history]);

	return (
		<div className={css(styleSheet.container)}>
			<MultiContainerHeader
				appBarHeader={
					<BreadcrumbBar
						items={[
							{ label: 'Data Boards', path: ROUTES.dataBoards.home },
							{ label: 'Matters', path: ROUTES.dataBoards.matters },
							{ label: 'Import', path: ROUTES.dataBoards.mattersImport },
						]}
					/>
				}
			/>

			{isUploading ? (
				<UploadStep
					isLoading={importMattersMutation.isPending}
					file={file}
					setFile={setFile}
					onDownloadSample={handleDownloadSample}
					sampleDownloadLabel='Download a sample spreadsheet to see the expected format'
				/>
			) : null}

			{isMapping && fields && headers && samples ? (
				<MapStep<Api.IImportLegalMatterFieldConfig>
					isLoading={finishImportMattersMutation.isPending}
					headers={headers}
					samples={samples}
					fields={fields}
					mappings={mappings}
					setMappings={setMappings}
					importError={importError}
					mappingErrors={mappingErrors}
					setMappingErrors={setMappingErrors}
					showMergeByNameCheckbox={true}
					mergeByName={mergeByName}
					onMergeByNameChange={onMergeByNameChange}
				/>
			) : null}

			{isProcessing && systemJob ? (
				<ProcessStep
					systemJob={systemJob}
					setSystemJob={setSystemJob}
					singularEntity='matter'
					pluralEntity='matters'
					onComplete={handleGoToMattersBoard}
					completeButtonLabel='Go to Matters Board'
				/>
			) : null}

			{!isProcessing ? (
				<div className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={next} disabled={!canGoNext}>
						<span>Next</span>
					</button>
				</div>
			) : null}
		</div>
	);
};
